import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Activity, 
  Upload, 
  Link2, 
  FileCheck, 
  CheckCircle, 
  RotateCcw,
  Clock,
  ChevronRight,
  RefreshCw,
  User,
  Filter
} from 'lucide-react';
import { cn } from '../lib/utils';
import { activityApi, getActiveWorkspace } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const CATEGORY_CONFIG = {
  all: { label: 'All Activity', icon: Activity, color: 'text-foreground' },
  imports: { label: 'Imports', icon: Upload, color: 'text-blue-600' },
  matching: { label: 'Matching', icon: Link2, color: 'text-green-600' },
  evidence: { label: 'Evidence', icon: FileCheck, color: 'text-purple-600' },
  approvals: { label: 'Approvals', icon: CheckCircle, color: 'text-amber-600' },
  rollback: { label: 'Rollbacks', icon: RotateCcw, color: 'text-red-600' },
};

function formatTimeAgo(timestamp) {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function ActivityIcon({ category }) {
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.all;
  const Icon = config.icon;
  return <Icon className={cn("w-4 h-4", config.color)} />;
}

function ActivityItem({ activity }) {
  const { description, user, timestamp, link, category } = activity;
  
  const getLinkUrl = () => {
    if (!link) return null;
    switch (link.type) {
      case 'case':
        return `/cases/${link.id}`;
      case 'import':
        return `/home`;
      case 'match_session':
        return `/home`;
      case 'filing':
        return `/reports`;
      default:
        return null;
    }
  };
  
  const linkUrl = getLinkUrl();
  
  return (
    <div className="group px-3 py-2.5 hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0">
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5">
          <ActivityIcon category={category} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground leading-snug line-clamp-2">
            {description}
          </p>
          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {user?.name || 'Unknown'}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTimeAgo(timestamp)}
            </span>
          </div>
        </div>
        {linkUrl && (
          <Link 
            to={linkUrl}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight className="w-4 h-4 text-muted-foreground hover:text-foreground" />
          </Link>
        )}
      </div>
    </div>
  );
}

export function WorkspaceActivityFeed({ className, collapsed = false }) {
  const { activeWorkspace } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [category, setCategory] = useState(null); // null = all
  const [error, setError] = useState(null);
  
  const loadActivities = useCallback(async (showRefresh = false) => {
    if (!getActiveWorkspace()) return;
    
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const response = await activityApi.getFeed(category, 30);
      setActivities(response.data.activities || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load activity feed:', err);
      setError('Failed to load activity');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [category]);
  
  // Load on mount and when category changes
  useEffect(() => {
    loadActivities();
  }, [loadActivities, activeWorkspace]);
  
  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => loadActivities(true), 30000);
    return () => clearInterval(interval);
  }, [loadActivities]);
  
  if (collapsed) {
    return (
      <div className={cn("py-2", className)}>
        <Button 
          variant="ghost" 
          size="icon"
          className="w-full"
          title="Recent Activity"
        >
          <Activity className="w-5 h-5" />
        </Button>
      </div>
    );
  }
  
  const selectedCategoryConfig = CATEGORY_CONFIG[category || 'all'];
  
  return (
    <div className={cn("flex flex-col h-full", className)} data-testid="workspace-activity-feed">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Recent Activity</span>
        </div>
        <div className="flex items-center gap-1">
          {/* Refresh button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="Refresh activity"
            onClick={() => loadActivities(true)}
            disabled={refreshing}
          >
            <RefreshCw className={cn("w-3.5 h-3.5", refreshing && "animate-spin")} />
          </Button>
          
          {/* Filter dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Filter activity">
                <Filter className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => setCategory(key === 'all' ? null : key)}
                  className={cn(
                    "flex items-center gap-2",
                    (category === key || (key === 'all' && !category)) && "bg-muted"
                  )}
                >
                  <config.icon className={cn("w-4 h-4", config.color)} />
                  <span>{config.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Active filter indicator */}
      {category && (
        <div className="px-3 py-1.5 bg-muted/50 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs">
            <selectedCategoryConfig.icon className={cn("w-3.5 h-3.5", selectedCategoryConfig.color)} />
            <span className="text-muted-foreground">Showing:</span>
            <span className="font-medium">{selectedCategoryConfig.label}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-5 px-1.5 text-xs"
            onClick={() => setCategory(null)}
          >
            Clear
          </Button>
        </div>
      )}
      
      {/* Activity list */}
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Loading activity...
          </div>
        ) : error ? (
          <div className="p-4 text-center text-sm text-red-500">
            {error}
          </div>
        ) : activities.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            No recent activity
          </div>
        ) : (
          <div>
            {activities.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))}
          </div>
        )}
      </ScrollArea>
      
      {/* Footer with count */}
      {activities.length > 0 && (
        <div className="px-3 py-1.5 border-t border-border text-xs text-muted-foreground text-center">
          {activities.length} recent events
        </div>
      )}
    </div>
  );
}
